import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useTitle } from "react-use";

import Centered from "../../components/ui/Centered";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import { ApiResponses, Unsuccessful } from "../../data/api-endpoints";
import t from "../../i18n";
import { useModal } from "../../providers/ModalProvider";
import { useApi, useApiData } from "../../use/api";
import useRedirect from "../../use/redirect";

export default function NotificationConfirmation() {
  const [searchParams] = useSearchParams();
  const unsubscribed = searchParams.get("unsubscribed") === "true";
  useTitle(unsubscribed ? t("Unsubscribed") : t("Confirm"));
  const redirect = useRedirect();
  const { confirm, openModal } = useModal();
  const [error, setError] = useState<
    undefined | Unsuccessful<ApiResponses["up.emailNotifications.confirmation.show"]>["error"]
  >();
  const { confirmation, isLoading } = useApiData(
    "up.emailNotifications.confirmation.show",
    {
      actionId: searchParams.get("actionId") || "",
      publicKey: searchParams.get("publicKey") || "",
      secretKey: searchParams.get("secretKey") || "",
    },
    {
      enable: !unsubscribed,
      onError: ({ error }) => {
        setError(error);
      },
    },
  );
  const { call: callConfirm } = useApi("up.emailNotifications.actions.do", {
    publicKey: searchParams.get("publicKey") || "",
    secretKey: searchParams.get("secretKey") || "",
  });

  useEffect(() => {
    if (!unsubscribed) return;
    void confirm({
      afterClose: () => {
        redirect(["up.root", {}]);
      },
      confirmText: t("Return home"),
      content: t("You will no longer receive email reminders about this."),
      title: t("Unsubscribed"),
    });
  }, [unsubscribed, confirm, redirect]);

  useEffect(() => {
    if (!error) return;

    switch (error) {
      case "invalid_authentication":
        void confirm({
          afterClose: () => {
            redirect(["up.root", {}]);
          },
          confirmText: t("Return home"),
          content: t("The link you clicked on is associated with a different account. Please log out and try again."),
          title: t("Make sure this is your link"),
        });
        return;
      case "notification_confirmation_not_required":
      case "notification_expired":
      case "notification_not_found":
        void confirm({
          afterClose: () => {
            redirect(["up.root", {}]);
          },
          confirmText: t("Return home"),
          content: t("The link you clicked on is wrong or expired"),
          title: t("Invalid link"),
        });
        return;
    }

    const exhaustCheck: never = error;
  }, [error, redirect, confirm]);

  useEffect(() => {
    if (!confirmation) return;
    openModal({
      cancelText: confirmation.alternative?.text,
      confirmText: confirmation.confirmText,
      content: confirmation.message,
      onClick: async (confirm: boolean) => {
        if (confirm || confirmation.alternative) {
          const response = await callConfirm({
            actionId: confirm ? searchParams.get("actionId")! : confirmation.alternative!.onClick,
          });
          if (response.success) {
            redirect(response.redirectUrl === false ? ["up.root", {}] : response.redirectUrl);
            return;
          }
        }

        redirect(["up.root", {}]);
      },
      title: t("Confirm"),
      type: confirmation.danger ? "warning" : undefined,
    });
  }, [confirmation, redirect, openModal, callConfirm, searchParams]);

  return <Centered>{isLoading && <LoadingSpinner />}</Centered>;
}
